import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import dayjs from 'dayjs';
import 'dayjs/locale/cs';
import { useRef } from 'react';
import { ImperativeDialog } from '@shared/components/dialogs/imperative-dialog';
import { ImperativeFormDialogRefType } from '@shared/components/UserFormDialog';
import {
  usePendingReservationsQuery,
  useApproveReservationMutation,
  useDenyReservationMutation,
} from './queries';
import { PendingReservation } from './api';
import { useIntlTranslations } from '@libs/intl/intl.hooks';

dayjs.locale('cs');

const PRIMARY = '#D02964';

const InfoRow = ({ icon, text }: { icon: React.ReactNode; text: string }) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>{icon}</Box>
    <Typography variant="body2" color="text.secondary">
      {text}
    </Typography>
  </Stack>
);

const ReservationCard = ({ reservation }: { reservation: PendingReservation }) => {
  const approveDialogRef = useRef<ImperativeFormDialogRefType | null>(null);
  const denyDialogRef = useRef<ImperativeFormDialogRefType | null>(null);
  const { mutate: approve, isPending: approving } = useApproveReservationMutation();
  const { mutate: deny, isPending: denying } = useDenyReservationMutation();
  const { t } = useIntlTranslations('pendingReservations');

  const date = dayjs(reservation.requestedDate);
  const dateTo = dayjs(reservation.requestedDateTo);
  const fullName = `${reservation.customerFirstName} ${reservation.customerLastName}`;

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: '14px',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: `${PRIMARY}10`,
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body1" fontWeight={700} color="text.primary">
            {fullName}
          </Typography>
          <Chip
            label={t('statusChip')}
            size="small"
            sx={{
              bgcolor: '#fff8e1',
              color: '#b8860b',
              border: '1px solid #f0c040',
              fontWeight: 600,
              fontSize: '0.7rem',
            }}
          />
        </Stack>
      </Box>

      {/* Body */}
      <Box sx={{ px: 2, py: 2 }}>
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CalendarMonthIcon sx={{ fontSize: 16, color: PRIMARY }} />
            <Typography variant="body2" fontWeight={600} color="text.primary">
              {date.format('dddd D. MMMM YYYY')}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <AccessTimeIcon sx={{ fontSize: 16, color: PRIMARY }} />
            <Typography variant="body2" color="text.secondary">
              {date.format('HH:mm')} – {dateTo.format('HH:mm')}
              {' · '}
              <Typography component="span" variant="body2" fontWeight={600} color="text.primary">
                {reservation.service.serviceName}
              </Typography>
              {' · '}
              {Number(reservation.service.basePrice).toLocaleString('cs-CZ')} Kč
            </Typography>
          </Stack>

          <Divider />

          <InfoRow
            icon={<PhoneIcon sx={{ fontSize: 15 }} />}
            text={`${reservation.customerPhonePrefix} ${reservation.customerPhone}`}
          />
          {reservation.customerEmail && (
            <InfoRow icon={<EmailIcon sx={{ fontSize: 15 }} />} text={reservation.customerEmail} />
          )}
          {reservation.note && (
            <Box
              sx={{
                bgcolor: '#f5f5f5',
                borderLeft: `3px solid ${PRIMARY}`,
                borderRadius: '0 8px 8px 0',
                px: 1.5,
                py: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {t('note')}
              </Typography>
              <Typography variant="body2" color="text.primary">
                {reservation.note}
              </Typography>
            </Box>
          )}

          {reservation.photos && reservation.photos.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
              {reservation.photos.map(photo => (
                <Box
                  key={photo.id}
                  component="a"
                  href={photo.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: '10px',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    display: 'block',
                  }}
                >
                  <img
                    src={photo.url}
                    alt={t('customerPhoto')}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>
              ))}
            </Stack>
          )}

          <Typography variant="caption" color="text.disabled">
            {t('received', { date: dayjs(reservation.createdAt).format('D. M. YYYY HH:mm') })}
          </Typography>
        </Stack>

        {/* Actions */}
        <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
          <Button
            variant="contained"
            startIcon={<CheckCircleIcon />}
            disabled={approving || denying}
            onClick={() => approveDialogRef.current?.open()}
            sx={{
              flex: 1,
              bgcolor: '#42D029',
              borderRadius: '10px',
              fontWeight: 700,
              '&:hover': { bgcolor: '#2db31a', color: '#fff' },
            }}
          >
            {approving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('approve')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            disabled={approving || denying}
            onClick={() => denyDialogRef.current?.open()}
            sx={{
              flex: 1,
              borderColor: '#D02929',
              color: '#D02929',
              borderRadius: '10px',
              fontWeight: 700,
              '&:hover': { bgcolor: '#D02929', color: '#fff', borderColor: '#D02929' },
            }}
          >
            {denying ? <CircularProgress size={18} sx={{ color: '#D02929' }} /> : t('deny')}
          </Button>
        </Stack>
      </Box>

      <ImperativeDialog
        ref={approveDialogRef}
        title={t('approveDialog.title')}
        dialogHelperText={t('approveDialog.helper', { name: fullName })}
        onConfirm={() => approve(reservation.id)}
      />
      <ImperativeDialog
        ref={denyDialogRef}
        title={t('denyDialog.title')}
        dialogHelperText={t('denyDialog.helper')}
        onConfirm={() => deny(reservation.id)}
      />
    </Box>
  );
};

const PendingReservationsPage = () => {
  const { data: reservations, isLoading, error } = usePendingReservationsQuery();
  const { t } = useIntlTranslations('pendingReservations');

  const pending = reservations?.filter(r => r.status === 'PENDING') ?? [];

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={700}>
          {t('title')}
        </Typography>
        {pending.length > 0 && <Badge badgeContent={pending.length} color="error" />}
      </Stack>

      {isLoading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress sx={{ color: PRIMARY }} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ borderRadius: '12px' }}>
          {t('errorLoad')}
        </Alert>
      )}

      {!isLoading && !error && pending.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 2,
            bgcolor: '#fff',
            borderRadius: '14px',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 48, color: '#42D029', mb: 1.5 }} />
          <Typography variant="body1" fontWeight={600} color="text.primary">
            {t('emptyTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('emptySubtitle')}
          </Typography>
        </Box>
      )}

      <Stack spacing={2}>
        {pending.map(r => (
          <ReservationCard key={r.id} reservation={r} />
        ))}
      </Stack>
    </Box>
  );
};

export default PendingReservationsPage;
